import {
  CANTON_FIX_SOURCE,
  CANTON_FIX_STATUS,
  formatParisDateTime,
  RECHARGE_ADMIN_URL,
  RECHARGE_FEATURE_MODE,
  type CantonFixListItem,
} from '@flux/shared';
import { useQuery } from '@tanstack/react-query';
import type { JSX } from 'react';
import { StatusBadge } from '@/components/ui/badge.js';
import { DescriptionItem, DescriptionList } from '@/components/ui/description-list.js';
import { ErrorState } from '@/components/ui/error-state.js';
import { ApiError } from '@/lib/api/client.js';
import { queryKeys } from '@/lib/query/keys.js';
import { fetchCantonFix } from './api.js';
import {
  Dash,
  DetailSection,
  DetailSkeleton,
  DetailSummary,
  ExternalRechargeLink,
} from './detail-parts.js';
import { DetailSheet } from './detail-sheet.js';

function CantonFixDetailBody({ fix }: { fix: CantonFixListItem }): JSX.Element {
  const status = CANTON_FIX_STATUS[fix.status];
  const locality = [fix.zip, fix.city].filter((part): part is string => part !== null && part !== '');

  return (
    <div className="flex flex-col gap-4">
      <DetailSummary title="Résumé">
        <DescriptionList>
          <DescriptionItem label="Statut">
            <span className="flex flex-wrap items-center gap-2">
              <StatusBadge registry={CANTON_FIX_STATUS} code={fix.status} />
              {status.description ? (
                <span className="text-muted-foreground">{status.description}</span>
              ) : null}
            </span>
          </DescriptionItem>
          <DescriptionItem label="Canton écrit">{fix.province ?? <Dash />}</DescriptionItem>
          <DescriptionItem label="Raison">{fix.reason ?? <Dash />}</DescriptionItem>
          <DescriptionItem label="Origine">
            <StatusBadge registry={CANTON_FIX_SOURCE} code={fix.source} />
          </DescriptionItem>
          <DescriptionItem label="Mode">
            <StatusBadge registry={RECHARGE_FEATURE_MODE} code={fix.mode} />
          </DescriptionItem>
          <DescriptionItem label="Modifiée le">
            <span className="whitespace-nowrap tabular-nums">
              {formatParisDateTime(new Date(fix.updatedAt))}
            </span>
          </DescriptionItem>
        </DescriptionList>
      </DetailSummary>

      <DetailSection title="Adresse">
        <DescriptionList>
          <DescriptionItem label="Adresse">
            {fix.address1 === null && locality.length === 0 ? (
              <Dash />
            ) : (
              <span className="flex flex-col">
                {fix.address1 === null ? null : <span>{fix.address1}</span>}
                {locality.length === 0 ? null : <span className="tabular-nums">{locality.join(' ')}</span>}
                <span>Suisse</span>
              </span>
            )}
          </DescriptionItem>
          <DescriptionItem label="Identifiant">
            <span className="tabular-nums">{fix.addressId}</span>
          </DescriptionItem>
          <DescriptionItem label="Client">
            {fix.customerId === null ? <Dash /> : <span className="tabular-nums">{fix.customerId}</span>}
          </DescriptionItem>
        </DescriptionList>
        {fix.candidates.length > 0 ? (
          <p className="text-sm">
            Cantons possibles : <span translate="no">{fix.candidates.join(', ')}</span>
          </p>
        ) : null}
      </DetailSection>

      {fix.customerId === null ? null : (
        <DetailSection title="Liens">
          <ExternalRechargeLink
            href={`${RECHARGE_ADMIN_URL}/customers/${encodeURIComponent(fix.customerId)}`}
          >
            Corriger l’adresse dans Recharge
          </ExternalRechargeLink>
        </DetailSection>
      )}
    </div>
  );
}

/**
 * Panneau de détail d'une correction de canton (`06` §8.2), ouvert par `?id=`.
 * Recharge n'a pas de page d'adresse : le lien mène à la fiche du client.
 */
export function CantonFixDetailSheet({
  id,
  onClose,
}: {
  id: string | null;
  onClose: () => void;
}): JSX.Element {
  const query = useQuery({
    queryKey: queryKeys.subscriptions.cantonFix(id ?? ''),
    queryFn: () => fetchCantonFix(id ?? ''),
    enabled: id !== null,
  });

  const fix = query.data;
  const error = query.error instanceof ApiError ? query.error : null;
  const isNotFound = error?.status === 404;

  return (
    <DetailSheet
      open={id !== null}
      onClose={onClose}
      title={fix ? `Adresse ${fix.addressId}` : 'Adresse suisse'}
    >
      {query.isPending ? (
        <DetailSkeleton label="Chargement de la correction" />
      ) : query.isError ? (
        <ErrorState
          message={error?.message ?? 'Impossible de charger cette correction.'}
          detail={error?.detail}
          {...(isNotFound
            ? {}
            : {
                onRetry: () => {
                  void query.refetch();
                },
              })}
        />
      ) : (
        <CantonFixDetailBody fix={query.data} />
      )}
    </DetailSheet>
  );
}
